import React from 'react';
import { KeyRound, Boxes, Plug, FileJson, Repeat, PlayCircle, AlertCircle, Globe } from 'lucide-react';
import { Code, CodeBlock, DocH, DocLi, DocList, DocP, DocSection, DocTable } from './docBits';

// Consumer-facing guide to the /api/v1 machine surface (doc-277). Every claim
// below was checked against backend/routes/service-api.js and
// backend/services/structured-dataset-service.js — if that code changes, this
// page is the thing that goes stale, so re-verify before editing.

export default function StructuredDataApi() {
  return (
    <>
      <DocSection title="Base URL and auth" icon={Globe}>
        <DocP>
          Two base URLs, same endpoints and same bearer-key auth on both — pick the one that
          matches where your code runs.
        </DocP>
        <DocTable
          head={['Calling from', 'Base URL']}
          rows={[
            [
              'Inside the cluster (Lighthouse and other Kubernetes services)',
              <Code key="internal">http://docket-backend.docket:3001/api/v1</Code>,
            ],
            [
              'Everywhere else',
              <Code key="public">https://docket.tooling.sparkinfra.net/api/v1</Code>,
            ],
          ]}
        />
        <DocP>
          Every request sends <Code>Authorization: Bearer dsk_…</Code> — no cookies, no sessions.
          Every endpoint is a <Code>GET</Code>.
        </DocP>
        <CodeBlock>{`curl -s https://docket.tooling.sparkinfra.net/api/v1/schemas \\
  -H "Authorization: Bearer dsk_your_key_here"`}</CodeBlock>
      </DocSection>

      <DocSection title="1. Get a key" icon={KeyRound}>
        <DocList>
          <DocLi>
            Keys are issued by an admin under <strong>Admin → Settings → API keys</strong>.
          </DocLi>
          <DocLi>
            The plaintext key is shown <strong>once</strong>, at creation (Docket stores only a
            hash) — put it straight into a secret manager.
          </DocLi>
          <DocLi>
            The scope <Code>read:structured_data</Code> covers the schema catalog and the
            structured-data outputs — sections 3 through 6 below.
          </DocLi>
          <DocLi>
            <Code>read:documents</Code> is a <strong>separate grant</strong>, not implicit in the
            one above. It covers the two contract-document endpoints only, and the issuing form
            leaves it <strong>unchecked</strong> by default — a key issued without naming scopes
            gets <Code>read:structured_data</Code> and nothing else. Ask for it explicitly if you
            need document access: scopes are fixed at issuance and there is no way to widen an
            existing key, so the fix for a missing scope is a new key.
          </DocLi>
          <DocLi>
            <Code>read:snapshot</Code> is for one job: loading a local Docket with this one&apos;s
            contract data (<Code>backend/scripts/pull-snapshot.js</Code>). It serves whole tables and
            whole files, restricted contracts included, and every page and file read is audited under
            the key. Mint a key for the pull and revoke it afterwards.
          </DocLi>
          <DocLi>
            Keys are revocable and stamp a last-used time. Use one key per consumer.
          </DocLi>
        </DocList>
      </DocSection>

      <DocSection title="2. Concepts" icon={Boxes}>
        <DocP>
          A <strong>schema</strong> defines the fields to extract from contracts. An{' '}
          <strong>output</strong> is one saved extraction — one schema run against one contract.
          Re-runs replace in place, so there is at most one output per contract + schema pair.
        </DocP>
        <DocP>
          Outputs carry per-field provenance (<Code>ai</Code> or <Code>human</Code>), a
          verification status set by legal, the exact prompt and model used, and staleness flags
          computed at read time.
        </DocP>
        <DocH>Which contracts this surface can see</DocH>
        <DocP style={{ marginBottom: 0 }}>
          Restricted contracts <strong>are</strong> included. Contracts still flagged{' '}
          <Code>needs_review</Code> in Docket are <strong>not</strong> — an unreviewed import is not
          trustworthy data yet. Such a contract is simply absent from the outputs listing, and the
          per-contract read answers <Code>404</Code> for it even when an output row exists. If your
          row count disagrees with what the app shows, this is usually why. The two document
          endpoints in section 3 are stricter still — they exclude restricted contracts too.
        </DocP>
      </DocSection>

      <DocSection title="3. Endpoints" icon={Plug}>
        <DocH style={{ marginTop: 0 }}>Current contract information</DocH>
        <DocP>
          <Code>GET /contracts</Code> returns the current saved contract fields, including contracts
          that have never had a schema extraction. Use your existing <Code>read:structured_data</Code> key.
          Filter by <Code>type</Code> (document type), <Code>category</Code> (business category), or{' '}
          <Code>status</Code> (contract status). Values are exact matches.
          For customer contracts, use <Code>category=Customer Agreement</Code>.
        </DocP>
        <CodeBlock label="Customer contracts, using current Docket fields">{`curl -sG "https://docket.tooling.sparkinfra.net/api/v1/contracts" \\
  --data-urlencode "category=Customer Agreement" \\
  -H "Authorization: Bearer dsk_your_key_here"`}</CodeBlock>
        <DocP>
          Returns <Code>{'{ contracts: [...], nextCursor }'}</Code>. Each contract has{' '}
          <Code>id</Code>, <Code>title</Code>, <Code>contractType</Code>, <Code>category</Code>,{' '}
          <Code>status</Code>, <Code>counterparty</Code>, <Code>effectiveDate</Code>,{' '}
          <Code>expirationDate</Code>, <Code>createdAt</Code>, and <Code>updatedAt</Code>.{' '}
          <Code>updated_since</Code> filters the contract update time. Default page size is 100;{' '}
          <Code>limit</Code> allows 1–500. Results are oldest update first: pass each returned{' '}
          <Code>nextCursor</Code> as <Code>cursor</Code> until it is null. Restricted contracts are
          included; contracts awaiting review are excluded. No extraction runs on this endpoint.
        </DocP>
        <DocH style={{ marginTop: 0 }}>Catalog</DocH>
        <DocList>
          <DocLi>
            <Code>GET /schemas</Code> — the catalog, with full field definitions.{' '}
            <strong>It returns every schema, including retired ones</strong>, each with an{' '}
            <Code>active</Code> boolean — filter on <Code>active</Code> unless you genuinely want
            dead schemas in your warehouse.
          </DocLi>
          <DocLi>
            <Code>GET /schemas/meta-schema</Code> — the JSON Schema of the definition format, for
            validating git-versioned schema definitions in CI.
          </DocLi>
        </DocList>

        <DocH>Keeping extraction results current</DocH>
        <DocP>
          In Data Schemas → Test &amp; run, users with the Run extractions permission can backfill
          missing results and retry failed or stale results. An admin can enable automatic
          processing per schema: every minute, Docket queues reviewed, matching contracts with a
          document and no saved result, including existing gaps. Failed runs need a manual retry;
          existing results are not automatically rerun. New results are unverified and are excluded
          from verified-only queries until reviewed. The schema&apos;s last-edited date describes
          its definition; each output has its own update time.
        </DocP>

        <DocH>One schema, across contracts</DocH>
        <DocP>
          <Code>GET /schemas/{'{schemaId}'}/outputs</Code> — one schema&apos;s outputs across every
          contract this surface can see. Rows include <Code>contractId</Code> and the contract
          title. Filters (a value outside the sets below, a non-ISO <Code>updated_since</Code>, or a
          malformed <Code>cursor</Code>, all answer <Code>400</Code>):
        </DocP>
        <DocTable
          head={['Query param', 'Values']}
          rows={[
            [<Code key="s">status</Code>, <span key="sv"><Code>pending</Code> · <Code>complete</Code> · <Code>failed</Code></span>],
            [<Code key="v">verification</Code>, <span key="vv"><Code>unreviewed</Code> · <Code>verified</Code></span>],
            [<Code key="u">updated_since</Code>, 'ISO timestamp'],
            [<Code key="l">limit</Code>, 'defaults to 100, capped at 500'],
            [<Code key="c">cursor</Code>, 'the nextCursor from the previous page'],
          ]}
        />
        <DocP>
          The response is <Code>{'{ schema: { id, name, version }, outputs: [...], nextCursor }'}</Code>.
          Pagination is cursor-based: follow <Code>nextCursor</Code> until it comes back{' '}
          <Code>null</Code>.
        </DocP>
        <CodeBlock label="First page of verified outputs">{`curl -s "https://docket.tooling.sparkinfra.net/api/v1/schemas/SCHEMA_ID/outputs?verification=verified&limit=100" \\
  -H "Authorization: Bearer dsk_your_key_here"`}</CodeBlock>

        <DocH>One contract, one schema</DocH>
        <DocP>
          <Code>GET /contracts/{'{contractId}'}/outputs/{'{schemaId}'}</Code> — one output with the
          full audit fields (including the exact prompt, which the list endpoint omits). Contract
          ids come from the app URL or from the outputs listing.
        </DocP>
        <DocTable
          head={['Status', 'Meaning']}
          rows={[
            [
              '200',
              'Served as stored. A fresh complete output is always served and never recomputed — and without ?compute=true a stale or failed output is served too.',
            ],
            [
              '202',
              <span key="a">
                Queued or in progress: <Code>{'{ status: "pending", jobRef }'}</Code>. Poll the same
                URL. A pending output answers 202 whether or not you passed{' '}
                <Code>compute=true</Code>. While a run is pending, the <strong>polled</strong> body
                also carries the last-good <Code>output</Code> when a previous run left values
                behind — but the response that <strong>starts</strong> a run carries only{' '}
                <Code>status</Code> and <Code>jobRef</Code>, with no <Code>output</Code> key at all.
                Read <Code>output</Code> defensively on a 202.
              </span>,
            ],
            [
              '400',
              <span key="c">
                Only reachable with <Code>?compute=true</Code>: the schema is inactive, or it does
                not apply to this contract&apos;s type/category. Targeting is checked{' '}
                <em>before</em> anything is enqueued, so a 400 means no run started.
              </span>,
            ],
            [
              '404',
              <span key="b">
                No output has ever been saved for this contract + schema (or an unknown id, or a
                contract this surface cannot see). Add <Code>?compute=true</Code> to enqueue a run.
              </span>,
            ],
          ]}
        />
        <DocP>
          <Code>compute=true</Code> is the only state-changing call on this surface: it enqueues
          when nothing fresh exists, refreshes when the output is stale, concurrent callers collapse
          to one job, and it never runs the model in the request path.
        </DocP>
        <CodeBlock label="Read one output, enqueueing a run if there is nothing saved yet">{`curl -s "https://docket.tooling.sparkinfra.net/api/v1/contracts/CONTRACT_ID/outputs/SCHEMA_ID?compute=true" \\
  -H "Authorization: Bearer dsk_your_key_here"`}</CodeBlock>

        <DocH>Contract documents</DocH>
        <DocP>
          Two endpoints read the contract&apos;s <strong>copy of record</strong> — the same
          executed-else-latest file the viewer and the extraction runs use, never a reference file.
          Both need the <Code>read:documents</Code> scope, which is a separate grant from{' '}
          <Code>read:structured_data</Code> (see section 1).
        </DocP>
        <DocList>
          <DocLi>
            <Code>GET /contracts/{'{contractId}'}/text</Code> — the extracted plain text, as{' '}
            <Code>
              {'{ contractId, fileId, fileHash, fileName, textStatus, extractedAt, text }'}
            </Code>
            .
          </DocLi>
          <DocLi>
            <Code>GET /contracts/{'{contractId}'}/document</Code> — the raw file bytes, with the
            real <Code>Content-Type</Code> and a{' '}
            <Code>Content-Disposition: attachment</Code> filename. Not base64 inside JSON.
          </DocLi>
        </DocList>
        <DocP>
          <Code>/text</Code> reports <em>why</em> there is no text instead of 404ing, so a pipeline
          can tell &ldquo;nothing extractable&rdquo; apart from &ldquo;no such contract&rdquo; and
          fall back to <Code>/document</Code>. Every value below answers <Code>200</Code>:
        </DocP>
        <DocTable
          head={['textStatus', 'Meaning']}
          rows={[
            [<Code key="ok">ok</Code>, <span key="okv">Text was extracted; <Code>text</Code> holds it.</span>],
            [<Code key="em">empty</Code>, 'Extraction succeeded but produced nothing — typically a scanned PDF with no text layer.'],
            [<Code key="fa">failed</Code>, 'Extraction ran and errored on this file.'],
            [<Code key="nb">no_bytes</Code>, 'The file row has no retrievable bytes to extract from.'],
            [
              <Code key="ms">missing</Code>,
              'No extraction row exists for this file yet. This is a read-only surface — the GET does not index on demand, so retry later rather than expecting it to fill in during the request.',
            ],
          ]}
        />
        <DocP>
          A <Code>404</Code> from either endpoint means something else entirely: no such contract,
          no contract this surface can see, or a contract with no non-reference file at all.
        </DocP>
        <DocP>
          <Code>fileId</Code> and <Code>fileHash</Code> are the correlation keys: they line up with
          an output&apos;s <Code>source_file_id</Code> and <Code>source_file_hash</Code>, so you can
          tell whether a text pull and a structured output describe the same bytes.
        </DocP>
        <DocP>
          <strong>These endpoints see less than the rest of this API</strong>, on purpose.
          Restricted contracts are excluded here — unlike <Code>/schemas/{'{id}'}/outputs</Code>,
          which includes them — and so are <Code>needs_review</Code> contracts. Both are 404
          as-if-absent. Extracted fields are a narrow reviewed projection of an agreement; whole
          text and raw bytes are not, and a service key has no user behind it.
        </DocP>
        <DocP style={{ marginBottom: 0 }}>
          <strong>What the plain text is not.</strong> It comes from pdf-parse and mammoth: no
          layout, no page structure, tables flattened into runs of text, and no OCR — so a scanned
          PDF comes back <Code>empty</Code> or <Code>failed</Code>, not as text. If layout or table
          structure matters to your consumer, pull <Code>/document</Code> and parse the bytes
          yourself.
        </DocP>
        <CodeBlock label="Pull the text, falling back to the raw file when there is none">{`base="https://docket.tooling.sparkinfra.net/api/v1"
auth="Authorization: Bearer dsk_your_key_here"
out=/tmp/docket-text.json

code=$(curl -s -o "$out" -w '%{http_code}' "$base/contracts/CONTRACT_ID/text" -H "$auth")
[ "$code" = "200" ] || { cat "$out"; exit 1; }   # 403 here = the key lacks read:documents.

case "$(jq -r '.textStatus' < "$out")" in
  ok) jq -r '.text' < "$out" > contract.txt ;;
  # empty | failed | no_bytes | missing — no usable text, so take the bytes instead.
  *)  curl -sf "$base/contracts/CONTRACT_ID/document" -H "$auth" -O -J ;;
esac`}</CodeBlock>
      </DocSection>

      <DocSection title="4. Reading an output" icon={FileJson}>
        <DocList>
          <DocLi>
            <Code>values</Code> is keyed by the schema&apos;s field keys.
          </DocLi>
          <DocLi>
            List fields are arrays of row objects. A row pulled from somewhere other than the
            contract&apos;s own agreement carries <Code>_source</Code>, naming that document — a
            reference file, a related contract, or a human edit. Rows read from the agreement itself
            have <strong>no</strong> <Code>_source</Code> key at all, so treat its absence as
            &ldquo;the agreement&rdquo; rather than reading <Code>row._source.type</Code> blind.
          </DocLi>
          <DocLi>
            <Code>provenance</Code> is per field: <Code>ai</Code> or <Code>human</Code>.
          </DocLi>
          <DocLi>
            <Code>{'stale: { schema, file }'}</Code> means the schema or the document changed after
            the run. The data is still served — the consumer decides what to do with it.
          </DocLi>
          <DocLi>
            <Code>orphaned: true</Code> means the schema no longer targets this contract — it was
            archived, retargeted onto a different type or category, or the contract&apos;s status
            (draft, terminated) is now outside the schema&apos;s scope. The values were extracted
            when the contract <em>was</em> in scope and are served unchanged. The same fact is an{' '}
            <Code>orphaned</Code> column in the CSV/JSON dataset export.
          </DocLi>
        </DocList>
        <DocP>
          Orphaned rows are <strong>never dropped</strong> from a feed, deliberately: the{' '}
          <Code>updated_since</Code> feed carries no delete signal, so a row that quietly vanished
          would sit in your warehouse forever with nothing to retract it. Filter on{' '}
          <Code>orphaned</Code> on your side — and treat it as a standing flag rather than a one-time
          event, since a schema can be retargeted back.
        </DocP>
      </DocSection>

      <DocSection title="5. Recipe: incremental sync of verified data" icon={Repeat}>
        <DocList>
          <DocLi>
            Pull <Code>/schemas</Code> once for the field definitions.
          </DocLi>
          <DocLi>
            Each run, page through{' '}
            <Code>/schemas/{'{id}'}/outputs?verification=verified&amp;updated_since={'{last run}'}</Code>,
            following <Code>nextCursor</Code> until it is <Code>null</Code>.
          </DocLi>
          <DocLi>Store the new high-water mark.</DocLi>
        </DocList>
        <DocP>
          Filtering on <Code>verification=verified</Code> means the dataset only ever contains rows
          legal signed off on.
        </DocP>
        <DocP>
          Check the HTTP code on <em>every</em> page, not just the first. A key revoked halfway
          through a long sync answers <Code>401</Code>, and a body with no{' '}
          <Code>nextCursor</Code> is indistinguishable from a clean last page — so a loop that only
          reads the JSON ends early, exits <Code>0</Code>, and hands you a truncated dataset that
          looks complete.
        </DocP>
        <CodeBlock label="The paging loop, in shape">{`cursor=""
page=/tmp/docket-page.json
while :; do
  url="https://docket.tooling.sparkinfra.net/api/v1/schemas/SCHEMA_ID/outputs"
  url="$url?verification=verified&updated_since=$LAST_RUN_ISO"
  [ -n "$cursor" ] && url="$url&cursor=$cursor"
  code=$(curl -s -o "$page" -w '%{http_code}' "$url" -H "Authorization: Bearer dsk_your_key_here")
  if [ "$code" != "200" ]; then
    cat "$page"   # 400/401/403 — stop loudly; a partial sync must not look clean.
    exit 1
  fi
  jq -c '.outputs[]' < "$page" >> outputs.ndjson
  cursor=$(jq -r '.nextCursor // empty' < "$page")
  [ -z "$cursor" ] && break
done`}</CodeBlock>
      </DocSection>

      <DocSection title="6. Recipe: trigger an extraction and wait for it" icon={PlayCircle}>
        <DocP>
          Reading is the common case, but a consumer can also ask for an extraction that has never
          run — or refresh a stale one. There is no POST: the same per-contract URL does both, and
          the model never runs inside your request.
        </DocP>
        <DocList>
          <DocLi>
            <strong>Enqueue</strong> —{' '}
            <Code>GET /contracts/{'{contractId}'}/outputs/{'{schemaId}'}?compute=true</Code>. You
            get <Code>202</Code> with a <Code>jobRef</Code> when a run starts, or a plain{' '}
            <Code>200</Code> with the output when a fresh complete one already exists (nothing runs
            in that case — that is the point).
          </DocLi>
          <DocLi>
            <strong>Poll</strong> — hit the <em>same</em> URL <strong>without</strong>{' '}
            <Code>compute=true</Code> every few seconds. It answers <Code>202</Code> while{' '}
            <Code>status</Code> is <Code>pending</Code>, then <Code>200</Code> once the run settles
            to <Code>complete</Code> or <Code>failed</Code>. Poll without{' '}
            <Code>compute=true</Code> so a slow run is never re-triggered underneath itself.
          </DocLi>
          <DocLi>
            <strong>Read</strong> — on <Code>complete</Code>, <Code>output.values</Code> is your
            data; on <Code>failed</Code>, <Code>output.error</Code> says why.
          </DocLi>
        </DocList>
        <DocP>
          <strong>It is safe to run this naively.</strong> Concurrent callers collapse onto one job
          rather than stacking runs, a fresh output is served rather than recomputed, and targeting
          is validated before anything is enqueued (an inactive schema or one that does not apply to
          the contract answers <Code>400</Code> — see the table above).
        </DocP>
        <DocP>
          <strong>But every run consumes AI capacity</strong> — a real model call over the
          contract&apos;s documents, with a real cost and a real rate limit shared with the rest of
          Docket. Use <Code>compute=true</Code> deliberately: for data that already exists, the
          incremental sync above costs nothing.
        </DocP>
        <CodeBlock label="Enqueue, then poll until it settles">{`base="https://docket.tooling.sparkinfra.net/api/v1"
auth="Authorization: Bearer dsk_your_key_here"
url="$base/contracts/CONTRACT_ID/outputs/SCHEMA_ID"
out=/tmp/docket-enqueue.json

# 1. Enqueue — 202 means a run started, 200 means a fresh output was already
#    there. Either way fall through: the poll below reads it.
code=$(curl -s -o "$out" -w '%{http_code}' "$url?compute=true" -H "$auth")
echo "enqueue: $code"
if [ "$code" != "200" ] && [ "$code" != "202" ]; then
  cat "$out"   # 400/401/404 — the body says why. Nothing was enqueued; stop.
  exit 1
fi

# 2. Poll the SAME url WITHOUT compute until the run settles.
#    60 attempts x sleep 5 = 5 minutes. Tune it — it is not a protocol limit.
for _ in $(seq 60); do
  body=$(curl -s "$url" -H "$auth")
  status=$(echo "$body" | jq -r '.output.status // "pending"')
  case "$status" in
    complete) echo "$body" | jq '.output.values'; exit 0 ;;
    failed)   echo "$body" | jq -r '.output.error'; exit 1 ;;
    *)        sleep 5 ;;
  esac
done

echo "timed out after 60 polls"
exit 1`}</CodeBlock>
      </DocSection>

      <DocSection title="7. Errors" icon={AlertCircle}>
        <DocTable
          head={['Status', 'Meaning']}
          rows={[
            [
              '400',
              <span key="bad">
                A rejected input, with the reason in the body: an unknown <Code>status</Code> or{' '}
                <Code>verification</Code> value, a non-ISO <Code>updated_since</Code>, a malformed{' '}
                <Code>cursor</Code>, or <Code>compute=true</Code> against a schema that is inactive
                or does not apply to the contract.
              </span>,
            ],
            ['401', 'Missing, unknown, or revoked key — the body says which.'],
            [
              '403',
              <span key="scope">
                Valid key, missing scope — <Code>read:structured_data</Code> for the schema and
                output endpoints, <Code>read:documents</Code> for <Code>/text</Code> and{' '}
                <Code>/document</Code>. The body names the scope that was missing.
              </span>,
            ],
            [
              '404',
              <span key="nf">
                Unknown contract or schema, a contract this surface cannot see, or no output has
                been saved yet. On the document endpoints it also covers a contract with no
                non-reference file — but never &ldquo;no text extracted&rdquo;, which is a{' '}
                <Code>200</Code> with a <Code>textStatus</Code>.
              </span>,
            ],
            ['202', 'Not an error — a run is in progress. Poll again.'],
          ]}
        />
        <DocP style={{ marginBottom: 0 }}>
          Need a key issued or revoked? Ask an admin — the panel lives under{' '}
          <strong>Admin → Settings → API keys</strong>.
        </DocP>
      </DocSection>
    </>
  );
}
